/**
 * Price filter for the collection and search sidebars.
 *
 * Shopify's price params are always store currency (AED), so AED is what the
 * hidden inputs carry and what data-range-floor / data-range-ceil hold. What
 * the shopper sees is a slider built in their selected currency.
 *
 * On every currency:changed the <collection-range-slider> is discarded and a
 * new one is created from the AED bounds, so the track, handles, number inputs
 * and tick labels can never be left showing amounts from the old currency.
 *
 * Exposes window.CollectionPriceFilter, which assets/collection-filters.js
 * calls for anything price related.
 */
(function () {
  var PRICELESS = 9999999;
  var DEFAULT_STEP = 100;
  var DEFAULT_TICKS = 7;
  // Round steps to pick from once the AED step is converted, so a 100 AED
  // step becomes 25 USD rather than 27.
  var STEP_CHOICES = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000];

  var extent = null;
  var extentPromise = null;

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function priceRanges(scope) {
    var found = (scope || document).querySelectorAll('[data-filter-range="price"]');
    return Array.prototype.slice.call(found);
  }

  // Without a rate the slider would show AED amounts under another currency's
  // heading, so stay on AED until currency-converter.js is ready.
  function currencyState() {
    var state = typeof window.getCurrencyState === 'function' ? window.getCurrencyState() : null;
    if (!state || !(state.rate > 0)) return { currency: 'AED', rate: 1, symbol: 'AED' };
    return state;
  }

  function ceilToStep(n, step) {
    n = parseFloat(n);
    if (!(n > 0)) return 0;
    return Math.ceil(n / step) * step;
  }

  function floorToStep(n, step) {
    n = parseFloat(n);
    if (!(n > 0)) return 0;
    return Math.floor(n / step) * step;
  }

  function roundToStep(n, step) {
    n = parseFloat(n);
    if (!(n > 0)) return 0;
    return Math.round(n / step) * step;
  }

  function clamp(n, lo, hi) {
    return Math.min(Math.max(n, lo), hi);
  }

  function niceStep(value) {
    if (!(value > 0)) return 1;
    var best = STEP_CHOICES[0];
    var bestGap = Infinity;
    STEP_CHOICES.forEach(function (choice) {
      var gap = Math.abs(Math.log(choice / value));
      if (gap < bestGap) {
        bestGap = gap;
        best = choice;
      }
    });
    return best;
  }

  // Whole-number tick labels: 500, 2,500, 11k, 68k, 4m. The unit comes from
  // the gap between ticks rather than each value, so a track only gets k or m
  // labels when rounding to them still leaves every tick distinct, and the
  // whole track is labelled in one unit.
  function tickFormatter(trackMax, tickCount) {
    var gap = trackMax / Math.max(tickCount - 1, 1);
    var unit = 1;
    var suffix = '';
    if (gap >= 1000000) {
      unit = 1000000;
      suffix = 'm';
    } else if (gap >= 1000) {
      unit = 1000;
      suffix = 'k';
    }
    return function (value) {
      if (!isFinite(value)) return '';
      var n = Math.round(value / unit);
      // Zero carries no unit. Only reachable as a true zero, since k and m are
      // used only when consecutive ticks are at least one whole unit apart.
      return n === 0 ? '0' : n.toLocaleString() + suffix;
    };
  }

  // AED step. Never read off the slider, whose step is in display currency.
  function baseStep(range) {
    var step = parseFloat(range.dataset.rangeStep);
    return step > 0 ? step : DEFAULT_STEP;
  }

  function productPrices(product) {
    var max = 0;
    var min = 0;
    (product.variants || []).forEach(function (variant) {
      var n = parseFloat(variant.price);
      if (isNaN(n) || n >= PRICELESS || n <= 0) return;
      if (n > max) max = n;
      if (min === 0 || n < min) min = n;
    });
    return { min: min, max: max };
  }

  // Shopify gives no range_min, and range_max is the Price on Request
  // placeholder when the priciest product has no price, so the collection's
  // real extent comes from products.json.
  function loadExtent(form) {
    if (extent) return Promise.resolve(extent);
    if (extentPromise) return extentPromise;

    var action = new URL(form.action, window.location.origin);
    if (action.pathname.indexOf('/collections/') === -1) {
      extent = { min: 0, max: 0 };
      return Promise.resolve(extent);
    }

    extentPromise = (async function () {
      var base = action.pathname.replace(/\/$/, '') + '/products.json?limit=250';

      async function scan(sort) {
        var res = await fetch(base + '&sort_by=' + sort + '&page=1');
        if (!res.ok) return { min: 0, max: 0 };
        var data = await res.json();
        var min = 0;
        var max = 0;
        (data.products || []).forEach(function (product) {
          var prices = productPrices(product);
          if (prices.max > max) max = prices.max;
          if (prices.min > 0 && (min === 0 || prices.min < min)) min = prices.min;
        });
        return { min: min, max: max };
      }

      var min = 0;
      var max = 0;
      try {
        var desc = await scan('price-descending');
        var asc = await scan('price-ascending');
        max = desc.max || asc.max;
        min = asc.min || desc.min;
      } catch (err) {
        console.warn('[collection-price-filter]', err);
      }
      extent = { min: min, max: max };
      return extent;
    })();

    return extentPromise;
  }

  function aedBounds(range, step) {
    var ceilAttr = parseFloat(range.dataset.rangeCeil);
    var floor = extent && extent.min > 0 ? floorToStep(extent.min, step) : 0;
    var ceil;
    if (ceilAttr > 0 && ceilAttr < PRICELESS) ceil = ceilAttr;
    else if (extent && extent.max > 0) ceil = ceilToStep(extent.max, step);
    else ceil = ceilAttr;
    if (ceil > 0 && ceil < PRICELESS) ceil = ceilToStep(ceil, step);
    if (!(ceil > floor)) return null;
    return { floor: floor, ceil: ceil };
  }

  // What the slider currently shows, in display currency.
  function trackBounds(range) {
    var slider = qs(range, 'collection-range-slider');
    var min = slider ? parseFloat(slider.getAttribute('min')) : NaN;
    var max = slider ? parseFloat(slider.getAttribute('max')) : NaN;
    if (isNaN(min)) min = parseFloat(range.dataset.rangeFloor) || 0;
    if (isNaN(max)) max = parseFloat(range.dataset.rangeCeil);
    return { min: min, max: max };
  }

  function replaceSlider(range, track) {
    var old = qs(range, 'collection-range-slider');
    var ticks = parseInt(old && old.getAttribute('tick-count'), 10) || DEFAULT_TICKS;
    var slider = document.createElement('collection-range-slider');
    slider.setAttribute('min', String(track.min));
    slider.setAttribute('max', String(track.max));
    slider.setAttribute('step', String(track.step));
    slider.setAttribute('value-min', String(track.lo));
    slider.setAttribute('value-max', String(track.hi));
    slider.setAttribute('tick-count', String(ticks));
    // Read by the component on first render, so set it before connecting.
    slider.tickFormatter = tickFormatter(track.max, ticks);
    if (old) old.replaceWith(slider);
    else range.appendChild(slider);
  }

  function updateSummary(range) {
    var filterEl = range.closest('.collection-filter');
    var label = filterEl && qs(filterEl, '[data-filter-selected]');
    if (!label) return;
    var slider = qs(range, 'collection-range-slider');
    var selected = slider && typeof slider.selected === 'function' ? slider.selected() : null;
    var track = trackBounds(range);
    var lo = selected ? parseFloat(selected.minSelected) : NaN;
    var hi = selected ? parseFloat(selected.maxSelected) : NaN;
    var untouched =
      !selected || !(track.max > track.min) || (lo <= track.min && hi >= track.max);
    var text = untouched
      ? ''
      : Math.round(lo).toLocaleString() + ' - ' + Math.round(hi).toLocaleString();
    label.textContent = text;
    if (text) label.removeAttribute('hidden');
    else label.setAttribute('hidden', '');
  }

  function build(range) {
    var minEl = qs(range, '[data-range-min]');
    var maxEl = qs(range, '[data-range-max]');
    if (!minEl || !maxEl) return;

    var step = baseStep(range);
    var aed = aedBounds(range, step);
    if (!aed) return;

    // The shopper's selection as Liquid or a drag last set it, kept separate
    // from the hidden inputs below. Those hold the on-grid amount matching
    // what is on screen, so re-deriving from this instead of from them stops
    // the selection creeping every time the currency is switched.
    var lo = parseFloat(range.dataset.rangeMinAed);
    var hi = parseFloat(range.dataset.rangeMaxAed);
    if (isNaN(lo)) lo = parseFloat(minEl.value);
    if (isNaN(hi)) hi = parseFloat(maxEl.value);
    if (isNaN(lo) || lo <= 0 || lo < aed.floor) lo = aed.floor;
    if (isNaN(hi) || hi <= 0 || hi > aed.ceil || hi >= PRICELESS) hi = aed.ceil;
    if (lo > hi) lo = aed.floor;

    range.dataset.rangeFloor = String(aed.floor);
    range.dataset.rangeCeil = String(aed.ceil);
    range.dataset.rangeMinAed = String(lo);
    range.dataset.rangeMaxAed = String(hi);

    var money = currencyState();
    var displayStep = niceStep(step * money.rate);
    var trackMin = floorToStep(aed.floor * money.rate, displayStep);
    var trackMax = ceilToStep(aed.ceil * money.rate, displayStep);
    if (!(trackMax > trackMin)) trackMax = trackMin + displayStep;

    // A handle sitting on an AED bound must land exactly on the track end,
    // otherwise rounding would read as a filter the shopper never set.
    var handleLo =
      lo <= aed.floor ? trackMin : clamp(roundToStep(lo * money.rate, displayStep), trackMin, trackMax);
    var handleHi =
      hi >= aed.ceil ? trackMax : clamp(roundToStep(hi * money.rate, displayStep), trackMin, trackMax);
    if (handleLo > handleHi) handleLo = handleHi;

    range.dataset.rangeRate = String(money.rate);
    range.dataset.rangeCurrency = money.currency;

    // Submit the exact AED equivalent of the amounts on screen, so the filter
    // can never disagree with what the shopper picked.
    minEl.value = handleLo <= trackMin ? aed.floor : Math.round(handleLo / money.rate);
    maxEl.value = handleHi >= trackMax ? aed.ceil : Math.round(handleHi / money.rate);

    replaceSlider(range, {
      min: trackMin,
      max: trackMax,
      step: displayStep,
      lo: handleLo,
      hi: handleHi
    });
    updateSummary(range);
  }

  function rebuildAll(scope) {
    priceRanges(scope).forEach(build);
  }

  function init(form) {
    var found = priceRanges(form);
    if (!found.length) return;
    // Build straight away so the shopper never sees AED amounts under a
    // heading naming another currency, then again once the extent lands.
    found.forEach(build);
    loadExtent(form).then(function () {
      rebuildAll(form);
    });
  }

  // Slider moved: translate the display amounts back into the AED the filter
  // params must carry.
  function apply(range, detail) {
    var minEl = qs(range, '[data-range-min]');
    var maxEl = qs(range, '[data-range-max]');
    if (!minEl || !maxEl) return;

    var rate = parseFloat(range.dataset.rangeRate);
    if (!(rate > 0)) rate = 1;
    var track = trackBounds(range);
    var floor = parseFloat(range.dataset.rangeFloor) || 0;
    var ceil = parseFloat(range.dataset.rangeCeil);
    var lo = parseFloat(detail && detail.minSelected);
    var hi = parseFloat(detail && detail.maxSelected);
    if (isNaN(lo)) lo = track.min;
    if (isNaN(hi)) hi = track.max;

    // Snap at the ends so an untouched handle submits the exact AED bound and
    // still gets dropped from the URL by disableDefaults().
    minEl.value = lo <= track.min ? floor : Math.round(lo / rate);
    maxEl.value = hi >= track.max ? ceil : Math.round(hi / rate);
    range.dataset.rangeMinAed = minEl.value;
    range.dataset.rangeMaxAed = maxEl.value;
    updateSummary(range);
  }

  function clear(range) {
    delete range.dataset.rangeMinAed;
    delete range.dataset.rangeMaxAed;
    var minEl = qs(range, '[data-range-min]');
    var maxEl = qs(range, '[data-range-max]');
    var floor = parseFloat(range.dataset.rangeFloor) || 0;
    var ceil = parseFloat(range.dataset.rangeCeil);
    if (minEl) minEl.value = floor;
    if (maxEl) maxEl.value = ceil;
    build(range);
  }

  // A handle left on a bound is not a filter, so keep it out of the URL.
  function disableDefaults(form) {
    priceRanges(form).forEach(function (range) {
      var minEl = qs(range, '[data-range-min]');
      var maxEl = qs(range, '[data-range-max]');
      if (!minEl || !maxEl) return;
      var floor = parseFloat(range.dataset.rangeFloor) || 0;
      var ceil = parseFloat(range.dataset.rangeCeil);
      minEl.disabled = parseFloat(minEl.value) <= floor;
      maxEl.disabled = parseFloat(maxEl.value) >= ceil;
    });
  }

  function enableAll(form) {
    form.querySelectorAll('[data-filter-range="price"] input').forEach(function (el) {
      el.disabled = false;
    });
  }

  // Fired by currency-converter.js on load, when live rates land, and on every
  // currency selection.
  document.addEventListener('currency:changed', function () {
    rebuildAll();
  });

  window.CollectionPriceFilter = {
    init: init,
    apply: apply,
    clear: clear,
    rebuild: rebuildAll,
    disableDefaults: disableDefaults,
    enableAll: enableAll
  };
})();
